import * as React from "react"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import {
  PageScrollContext,
  useBuildPageScrollContext,
} from "@digits-shared/components/UI/Elements/PageScrollContext"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import styled, { css } from "styled-components"
import { ApplicationFullWidthPane } from "src/frontend/components/OS/Chrome/Desktop/ApplicationPane"
import { HomeHeader } from "src/frontend/components/OS/Home/HomeHeader"
import { PortalContent } from "src/frontend/components/OS/Home/PortalContent"
import { Desktop } from "src/frontend/components/Shared/Responsive/Desktop"
import { Mobile } from "src/frontend/components/Shared/Responsive/Mobile"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

const BORDER_RADIUS = css`
  border-radius: 16px 16px 0 0;
`

/*
  STYLES
*/

const HomeChrome = styled.div`
  display: flex;
  flex-direction: column;
  height: var(--viewport-height);
  width: 100%;
`

const HomeContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`

const ScrollWrapper = styled.div`
  ${BORDER_RADIUS};
  overflow: hidden;
`

const ScrollableContainer = styled.div`
  ${BORDER_RADIUS};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;

  height: calc(100vh - 111px);
  width: 100%;
  padding-bottom: 30px;
  margin-top: calc(var(--header-height) + var(--header-gap));
`

/**
 * Fixes the horizontal offset that happens in the presence of a permanent
 * scroll bar (comes from having a wired mouse).
 *
 * https://stackoverflow.com/a/30293718
 */
export const ScrollBarHorizontalAdjustment = styled.div`
  height: fit-content;
  width: 100%;
  border-radius: 16px;
`

/*
 COMPONENTS
*/

export const HomeContent: React.FC<React.PropsWithChildren> = () => {
  const { isSharingContextActive, hasDashboardLegalEntityOrAffiliation, currentLegalEntity } =
    useSession<FrontendSession>()
  const { search } = useRouter()
  const generatePath = useFrontendPathGenerator()

  if (!currentLegalEntity) return null

  if (search.has("intervalCount") || search.has("highlight")) {
    return (
      <LoggedRedirect
        name="home-removeIntervalCount"
        to={generatePath(routes.legalEntityHome, {}, "includeAllQueryParams", [
          "intervalCount",
          "highlight",
        ])}
      />
    )
  }

  /* Need to ensure a legal entity to handle loading support for Digits Employees */
  if (!hasDashboardLegalEntityOrAffiliation || isSharingContextActive) {
    return null
  }

  return (
    <>
      <Desktop>
        <ApplicationFullWidthPane>
          <HomeChrome>
            <HomeContainer>
              <HomeHeader />
              <DesktopContent />
            </HomeContainer>
          </HomeChrome>
        </ApplicationFullWidthPane>
      </Desktop>

      <Mobile>
        <PortalContent />
      </Mobile>
    </>
  )
}

const DesktopContent: React.FC = () => {
  const { scrollElement: scrollContainer, pageScrollContext } = useBuildPageScrollContext()

  return (
    <ScrollWrapper>
      <PageScrollContext.Provider value={pageScrollContext}>
        <ScrollableContainer ref={scrollContainer}>
          <ScrollBarHorizontalAdjustment>
            <PortalContent />
          </ScrollBarHorizontalAdjustment>
        </ScrollableContainer>
      </PageScrollContext.Provider>
    </ScrollWrapper>
  )
}
